import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { Declaration, DeclarePayload } from '../../core/entity/declaration.types';
import { DeclarationListKind } from '../declarations-panel/declaration-specs';
import { ModalComponent } from '../modal/modal';

/**
 * What the dialog is being opened to do.
 *
 * 🚨 `CORRECT` is **not** an edit, and the copy must never call it one
 * (`N8`, `.claude/skills/append-only-records`). The register is append-only:
 * correcting an entry withdraws the row and declares a new one, and BOTH stay
 * on record attributed to whoever made each act. The dialog therefore says so
 * on screen — a form that looks like an edit but silently appends a retraction
 * is the same lie in a friendlier font.
 */
export type DeclarationDialogPurpose = 'ADD' | 'CORRECT';

export interface DeclarationDialogData {
  readonly kind: DeclarationListKind;
  readonly purpose: DeclarationDialogPurpose;
  /** The row being corrected — present only when `purpose` is `CORRECT`. */
  readonly row?: Declaration;
}

/** What the caller gets back. `null`/undefined on cancel — nothing was written. */
export interface DeclarationDialogResult {
  readonly payload: DeclarePayload;
  /** The row to withdraw first. Set only on a correction. */
  readonly corrects?: Declaration;
}

const TITLES: Record<DeclarationListKind, { add: string; correct: string }> = {
  FUNDER: { add: 'Add a funder', correct: 'Correct this funder' },
  RELATED_PARTY: { add: 'Add a related party', correct: 'Correct this related party' },
  DIRECTOR: { add: 'Add a director', correct: 'Correct this director' },
};

/**
 * The add/correct form for a declared funder, related party or director
 * (FLOW-243 lists, moved off the panel into a modal).
 *
 * **It builds a payload; it does not write.** The panel owns the write, because
 * the panel knows which door to use — `client` declares the business's own
 * statement, `ops` records what CapX found, and those are different acts stored
 * differently. A dialog that wrote directly would have to know that too, and
 * then there would be two places to get it wrong.
 */
@Component({
  selector: 'app-declaration-dialog',
  imports: [
    ModalComponent,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: './declaration-dialog.html',
  styleUrl: './declaration-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeclarationDialogComponent {
  protected readonly data = inject<DeclarationDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<DeclarationDialogComponent, DeclarationDialogResult>>(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  protected readonly isCorrection = this.data.purpose === 'CORRECT';

  protected get title(): string {
    const t = TITLES[this.data.kind];
    return this.isCorrection ? t.correct : t.add;
  }

  protected get saveLabel(): string {
    return this.isCorrection ? 'Correct the entry' : 'Add to the list';
  }

  protected readonly facilityTypes = [
    { value: 'DEBTOR_FINANCE', label: 'Debtors Finance' },
    { value: 'TERM_LOAN', label: 'Term loan' },
    { value: 'OVERDRAFT', label: 'Overdraft' },
    { value: 'ASSET_FINANCE', label: 'Asset finance' },
    { value: 'OTHER', label: 'Other' },
  ];

  /**
   * Four states, and "Not sure" is one of them — never a yes/no. A client who
   * does not know whether a facility is settled must be able to say so; forcing
   * the answer manufactures a fact nobody stated.
   */
  protected readonly facilityStates = [
    { value: 'LIVE', label: 'Still running' },
    { value: 'SETTLED', label: 'Settled' },
    { value: 'NOT_SURE', label: 'Not sure' },
  ];

  protected readonly relationships = [
    { value: 'HOLDING', label: 'Holding company' },
    { value: 'SUBSIDIARY', label: 'Subsidiary' },
    { value: 'COMMON_DIRECTOR', label: 'Shared director' },
    { value: 'FAMILY', label: 'Family' },
    { value: 'OTHER', label: 'Other' },
  ];

  protected readonly directorRoles = [
    { value: 'DIRECTOR', label: 'Director' },
    { value: 'SHAREHOLDER', label: 'Shareholder' },
    { value: 'BOTH', label: 'Director and shareholder' },
  ];

  protected readonly form = this.fb.group({
    // FUNDER
    funder_name: [''],
    facility_type: [''],
    facility_state: [''],
    // RELATED_PARTY
    party_name: [''],
    relationship: [''],
    // FUNDER + RELATED_PARTY — comma-separated on screen, a list on the wire.
    payer_names: [''],
    // DIRECTOR
    full_name: [''],
    id_number: [''],
    director_role: [''],
  });

  constructor() {
    const row = this.data.row;
    if (row) this.prefill(row);
  }

  /** A correction opens on what is there now, so the person edits words not memory. */
  private prefill(row: Declaration): void {
    this.form.patchValue({
      funder_name: row.funder_name ?? '',
      facility_type: row.facility_type ?? '',
      facility_state: row.facility_state ?? '',
      party_name: row.party_name ?? '',
      relationship: row.relationship ?? '',
      payer_names: (row.payer_names ?? []).join(', '),
      full_name: row.full_name ?? '',
      id_number: row.id_number ?? '',
      director_role: row.director_role ?? '',
    });
  }

  protected submit(): void {
    const payload = this.payload();
    if (payload === null) {
      const name = {
        FUNDER: 'funder_name',
        RELATED_PARTY: 'party_name',
        DIRECTOR: 'full_name',
      } as const;
      const control = this.form.controls[name[this.data.kind]];
      control.setErrors({ required: true });
      control.markAsTouched();
      return;
    }
    this.dialogRef.close({
      payload,
      ...(this.isCorrection && this.data.row ? { corrects: this.data.row } : {}),
    });
  }

  /** `null` when the one required fact — the name — is absent. */
  private payload(): DeclarePayload | null {
    const v = this.form.getRawValue();
    const payerNames = (v.payer_names ?? '')
      .split(',')
      .map((n) => n.trim())
      .filter((n) => n.length > 0);

    if (this.data.kind === 'FUNDER') {
      const name = (v.funder_name ?? '').trim();
      if (!name) return null;
      return {
        kind: 'FUNDER',
        funder_name: name,
        ...(v.facility_type ? { facility_type: v.facility_type } : {}),
        ...(v.facility_state ? { facility_state: v.facility_state } : {}),
        ...(payerNames.length ? { payer_names: payerNames } : {}),
      } as DeclarePayload;
    }

    if (this.data.kind === 'RELATED_PARTY') {
      const name = (v.party_name ?? '').trim();
      if (!name) return null;
      return {
        kind: 'RELATED_PARTY',
        party_name: name,
        ...(v.relationship ? { relationship: v.relationship } : {}),
        ...(payerNames.length ? { payer_names: payerNames } : {}),
      } as DeclarePayload;
    }

    const name = (v.full_name ?? '').trim();
    if (!name) return null;
    return {
      kind: 'DIRECTOR',
      full_name: name,
      ...(v.id_number ? { id_number: v.id_number.trim() } : {}),
      ...(v.director_role ? { director_role: v.director_role } : {}),
    } as DeclarePayload;
  }
}
